import React, { useEffect, useMemo, useRef, useState } from "react";

// ASSETS
import Illustration from "assets/icons/Deco corner homepage _ About Board DSU_.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import { ReactComponent as NoData } from "assets/svg/NoData.svg";

// STYLES
import {
  ArticleBodyWrapper,
  ArticleContent,
  ArticleRow,
  ArticleTitleWrapper,
  Body,
  ButtonContainer,
  ImageCard,
  Info,
  LeftSideWrapper,
  Line,
  ParentContainer,
  RightSideWrapper,
  Section,
  SectionIllustration,
  SmallImageCard,
  TabsWrapper,
  Title,
  NoDataContainer,
  SvgContainer,
} from "./ArticlesSectionStyled";

// LIBRARIES
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MISC
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { HeaderTabTypes } from "components/HeaderTab/HeaderTabModel";
import { appRoutes } from "config/AppRoutesConfig";
import { COLORS } from "constants/variables";
import useWindowWidth from "hooks/useWindowWidth";
import { IProps } from "./ArticlesSectionModel";
import { uploadBaseUrl } from "services/base";

// REDUX
import { useSelector } from "react-redux";
import { selectLanguage } from "slices/LanguageSlice/languageSlice";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import HeaderTab from "components/HeaderTab/HeaderTab";
import homeService from "services/homeService";

const ArticlesSection: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedLanguage = useSelector(selectLanguage);
  const windowWidth = useWindowWidth();

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [newsCategories, setNewsCategories] = useState<any>();
  const [visibleArticles, setVisibleArticles] = useState<any[]>([]);
  const [articlesToShow, setArticlesToShow] = useState<number>(5);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const rightSideWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    homeService
      .getNewsArticlesByCategory()
      .then((response) => setNewsCategories(response))
      .catch((err) => console.error(err));
  }, []);

  const sortedArticles = useMemo(() => {
    const all =
      newsCategories?.data?.[selectedTab]?.attributes?.news_articles_ids
        ?.data ?? [];
    return [...all].sort(
      (a, b) =>
        new Date(b.attributes.article_date).getTime() -
        new Date(a.attributes.article_date).getTime()
    );
  }, [newsCategories, selectedTab]);

  useEffect(() => {
    setVisibleArticles(sortedArticles.slice(0, articlesToShow));
  }, [sortedArticles, articlesToShow]);

  useEffect(() => {
    if (!rightSideWrapperRef.current || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setArticlesToShow((prev) => prev + 5);
        }
      },
      {
        root: rightSideWrapperRef.current,
        rootMargin: "100px",
        threshold: 1.0,
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [rightSideWrapperRef.current, loadMoreRef.current]);

  const formatDateToMonthDD = (date: string) => {
    const d = new Date(date);
    const day = d.getDate();
    const year = d.getFullYear();
    const locale = selectedLanguage === "ro" ? "ro-RO" : "en-US";
    const weekday = d.toLocaleDateString(locale, { weekday: "long" });
    const month = d.toLocaleDateString(locale, { month: "long" });
    return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${day} ${
      month.charAt(0).toUpperCase() + month.slice(1)
    } ${year}`;
  };

  const articlesSectionContent =
    sortedArticles.length > 0 ? (
      <>
        <LeftSideWrapper>
          <ImageCard
            src={
              sortedArticles[0]?.attributes?.cover?.data?.attributes?.url?.trim()
                ? `${uploadBaseUrl}${sortedArticles[0]?.attributes?.cover?.data?.attributes?.url}`
                : CoverPlaceholder
            }
            alt=""
          />
          <Body>
            <CustomText
              text={sortedArticles[0]?.attributes?.title}
              type={TextType.H2}
              color={COLORS.Cultured}
            />
            <CustomText
              text={sortedArticles[0]?.attributes?.teaser}
              type={TextType.P1}
              color={COLORS.Cultured}
            />
            <Info>
              <CustomText
                text={
                  sortedArticles[0]?.attributes?.publishedAt
                    ? formatDateToMonthDD(
                        sortedArticles[0].attributes.publishedAt
                      )
                    : ""
                }
                type={TextType.P2}
                color={COLORS.Cultured}
              />
            </Info>
          </Body>
          <ButtonContainer>
            <ButtonAtom
              text={t("view.articleSection.button")}
              type={ButtonType.ArrowButton}
              rightIcon={true}
              onClick={() =>
                navigate(
                  selectedTab === 0
                    ? appRoutes.officialsDetails + sortedArticles[0]?.id
                    : appRoutes.newsAndPartnersDetails + sortedArticles[0]?.id
                )
              }
            />
          </ButtonContainer>
        </LeftSideWrapper>
        <Line />
        <RightSideWrapper ref={rightSideWrapperRef}>
          {visibleArticles.slice(1).map((article, index) => (
            <ArticleRow
              key={index}
              onClick={() =>
                navigate(
                  selectedTab === 0
                    ? appRoutes.officialsDetails + article?.id
                    : appRoutes.newsAndPartners + article?.id
                )
              }
            >
                <SmallImageCard
                  src={
                    article?.attributes?.cover?.data?.attributes?.url?.trim()
                      ? `${uploadBaseUrl}${article?.attributes?.cover?.data?.attributes?.url}`
                      : CoverPlaceholder
                  }
                  alt=""
                />
              <ArticleContent>
                <ArticleTitleWrapper>
                  <CustomText
                    text={
                      article?.attributes?.publishedAt
                        ? formatDateToMonthDD(
                            sortedArticles[0].attributes.publishedAt
                          )
                        : ""
                    }
                    type={TextType.P2}
                    color={COLORS.Cultured}
              />
                  <CustomText
                    text={article?.attributes?.title}
                    type={TextType.H4}
                    color={COLORS.Cultured}
                  />
                </ArticleTitleWrapper>
                <ArticleBodyWrapper>
                  <CustomText
                    text={article?.attributes?.teaser}
                    type={TextType.P2}
                    color={COLORS.Cultured}
                  />
                </ArticleBodyWrapper>
                <ButtonContainer>
                  <ButtonAtom
                    text={t("view.articleSection.button")}
                    type={ButtonType.ArrowButton}
                    rightIcon={true}
                    onClick={() =>
                      navigate(
                        selectedTab === 0
                          ? appRoutes.officialsDetails + article?.id
                          : appRoutes.newsAndPartners + article?.id
                      )
                    }
                  />
                </ButtonContainer>
              </ArticleContent>
            </ArticleRow>
          ))}
          <div ref={loadMoreRef} />
        </RightSideWrapper>
      </>
    ) : (
      <NoDataContainer>
        <SvgContainer>
          <NoData />
        </SvgContainer>
        <ArticleTitleWrapper>
          <CustomText
            text={t("app.noData")}
            type={TextType.H4}
            color={COLORS.Cultured}
          />
        </ArticleTitleWrapper>
      </NoDataContainer>
    );

  const tabs = [
    {
      id: 0,
      tabTitle: t("view.articleSection.tabs.press"),
      content: articlesSectionContent,
    },
  ];

  return (
    <ParentContainer>
      <SectionIllustration src={Illustration} alt="" />
      <Title>
        <CustomText
          text={t("view.articleSection.titles")}
          type={TextType.H2}
          color={COLORS.Yellow}
        />
      </Title>
      <TabsWrapper>
        <HeaderTab
          tabs={tabs}
          onTabSelect={setSelectedTab}
          type={HeaderTabTypes.WhiteTheme}
        />
      </TabsWrapper>
      <Section>{tabs[selectedTab].content}</Section>
    </ParentContainer>
  );
};

export default ArticlesSection;
